import React, { useState, useEffect, useCallback } from 'react';
import Icon from '../../../components/AppIcon';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';
import DateRangeFilter, { getDateRange } from '../../../components/DateRangeFilter';

const SUPABASE_URL = import.meta.env?.VITE_SUPABASE_URL;
const SUPABASE_ANON_KEY = import.meta.env?.VITE_SUPABASE_ANON_KEY;

async function sendSellOfferEmail(eventType, sellerEmail, offerData) {
  if (!sellerEmail) return;
  try {
    await fetch(`${SUPABASE_URL}/functions/v1/send-sell-offer-email`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
      },
      body: JSON.stringify({ eventType, sellerEmail, offerData }),
    });
  } catch (err) {
    console.error('Email notification error:', err);
  }
}

// Status display config — maps DB availability_status values to UI labels
const STATUS_CONFIG = {
  'reserved': { label: 'Pending for Approval', bg: '#fef3c7', color: '#92400e', dot: '#f59e0b' },
  'pending_approval': { label: 'Pending for Approval', bg: '#fef3c7', color: '#92400e', dot: '#f59e0b' },
  'available': { label: 'Approved & Live', bg: '#d1fae5', color: '#065f46', dot: '#10b981' },
  'paused': { label: 'Paused', bg: '#fef9c3', color: '#713f12', dot: '#ca8a04' },
  'withdrawn': { label: 'Withdrawn', bg: '#f3f4f6', color: '#374151', dot: '#9ca3af' },
  'rejected': { label: 'Rejected', bg: '#fee2e2', color: '#991b1b', dot: '#ef4444' },
  'sold': { label: 'Withdrawn', bg: '#f3f4f6', color: '#374151', dot: '#9ca3af' },
};

const daysDiff = (origDateStr, newDateStr) => {
  if (!origDateStr || !newDateStr) return 0;
  return Math.round((new Date(newDateStr) - new Date(origDateStr)) / (1000 * 60 * 60 * 24));
};

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  const d = new Date(dateStr + 'T00:00:00');
  if (isNaN(d?.getTime())) return dateStr;
  const day = String(d?.getDate())?.padStart(2, '0');
  const month = d?.toLocaleString('en-IN', { month: 'short' });
  return `${day} ${month} ${d?.getFullYear()}`;
};

const mapQuotaToOffer = (quota) => {
  const status = quota?.availability_status || 'reserved';
  return {
    id: quota?.offer_id || quota?.id,
    dbId: quota?.id,
    factoryName: quota?.factory_name || '—',
    grade: quota?.grade || '—',
    quantity: parseFloat(quota?.quantity) || 0,
    offeredRate: parseFloat(quota?.price_per_ton) || 0,
    quality: quota?.quality_remarks || 'Standard',
    paymentByDate: quota?.available_from_date || '',
    liftingByDate: quota?.offer_valid_until || '',
    offerValidity: quota?.offer_valid_until
      ? Math.max(0, Math.round((new Date(quota.offer_valid_until) - new Date()) / (1000 * 60 * 60 * 24)))
      : null,
    status,
    sellerEmail: quota?.seller_email || '',
    isResumeRequest: quota?.is_resume_request === true,
    created_at: quota?.created_at || null,
  };
};

// ─── Pause Confirmation Modal ──────────────────────────────────────────────────
const PauseModal = ({ offer, onConfirm, onCancel }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.45)' }}>
    <div className="w-full max-w-md rounded-2xl shadow-2xl p-6" style={{ background: '#fffbf5', border: '1px solid #f3e8d0' }}>
      <div className="flex items-center justify-center mb-4">
        <div className="w-14 h-14 rounded-full flex items-center justify-center" style={{ background: '#fef9c3' }}>
          <Icon name="PauseCircle" size={28} color="#ca8a04" />
        </div>
      </div>
      <h3 className="text-lg font-bold text-center mb-1" style={{ color: '#1a1a1a' }}>Pause Offer?</h3>
      <p className="text-xs text-center font-semibold mb-1" style={{ color: '#ca8a04' }}>Temporary hide — can resume anytime</p>
      <p className="text-sm text-center mb-1" style={{ color: '#6b7280' }}>
        Offer ID: <span className="font-semibold" style={{ color: '#1a1a1a' }}>{offer?.id}</span>
      </p>
      <p className="text-sm text-center mb-5" style={{ color: '#6b7280' }}>
        {offer?.factoryName} — {offer?.grade} — {offer?.quantity?.toLocaleString()} MT
      </p>
      <div className="rounded-xl p-4 mb-5 text-sm" style={{ background: '#fefce8', border: '1px solid #fde047', color: '#713f12' }}>
        <p className="font-semibold mb-1">What happens when you Pause:</p>
        <ul className="space-y-1 text-xs list-disc list-inside">
          <li>Offer is temporarily hidden from the Marketplace</li>
          <li>You can resume it anytime — no re-approval needed</li>
          <li>Your offer details and rate are preserved</li>
        </ul>
      </div>
      <div className="flex gap-3">
        <button onClick={onCancel} className="flex-1 py-2.5 rounded-xl text-sm font-semibold" style={{ background: '#f3f4f6', color: '#374151', border: '1px solid #e5e7eb' }}>
          Cancel
        </button>
        <button onClick={onConfirm} className="flex-1 py-2.5 rounded-xl text-sm font-bold text-white shadow-md" style={{ background: 'linear-gradient(135deg, #ca8a04, #a16207)' }}>
          Yes, Pause Temporarily
        </button>
      </div>
    </div>
  </div>
);

// ─── Withdraw Confirmation Modal ───────────────────────────────────────────────
const WithdrawModal = ({ offer, onConfirm, onCancel }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.45)' }}>
    <div className="w-full max-w-md rounded-2xl shadow-2xl p-6" style={{ background: '#fffbf5', border: '1px solid #f3e8d0' }}>
      <div className="flex items-center justify-center mb-4">
        <div className="w-14 h-14 rounded-full flex items-center justify-center" style={{ background: '#fee2e2' }}>
          <Icon name="AlertTriangle" size={28} color="#dc2626" />
        </div>
      </div>
      <h3 className="text-lg font-bold text-center mb-1" style={{ color: '#1a1a1a' }}>Withdraw Offer?</h3>
      <p className="text-xs text-center font-semibold mb-1" style={{ color: '#dc2626' }}>Strong removal — serious action</p>
      <p className="text-sm text-center mb-1" style={{ color: '#6b7280' }}>
        Offer ID: <span className="font-semibold" style={{ color: '#1a1a1a' }}>{offer?.id}</span>
      </p>
      <p className="text-sm text-center mb-5" style={{ color: '#6b7280' }}>
        {offer?.factoryName} — {offer?.grade} — {offer?.quantity?.toLocaleString()} MT
      </p>
      <div className="rounded-xl p-4 mb-5 text-sm" style={{ background: '#fff1f2', border: '1px solid #fecdd3', color: '#9a3412' }}>
        <p className="font-semibold mb-1">⚠️ What happens when you Withdraw:</p>
        <ul className="space-y-1 text-xs list-disc list-inside">
          <li>Offer is permanently removed from the Marketplace</li>
          <li>To relist, you must submit a Resume request</li>
          <li>Quota Manager re-approval will be required</li>
        </ul>
      </div>
      <div className="flex gap-3">
        <button onClick={onCancel} className="flex-1 py-2.5 rounded-xl text-sm font-semibold" style={{ background: '#f3f4f6', color: '#374151', border: '1px solid #e5e7eb' }}>
          Cancel
        </button>
        <button onClick={onConfirm} className="flex-1 py-2.5 rounded-xl text-sm font-bold text-white shadow-md" style={{ background: 'linear-gradient(135deg, #ef4444, #dc2626)' }}>
          Yes, Withdraw Permanently
        </button>
      </div>
    </div>
  </div>
);

// ─── Resume Modal ──────────────────────────────────────────────────────────────
const ResumeModal = ({ offer, onConfirm, onCancel }) => {
  const [newPaymentByDate, setNewPaymentByDate] = useState(offer?.paymentByDate || '');
  const [newLiftingByDate, setNewLiftingByDate] = useState(offer?.liftingByDate || '');

  const paymentDiff = daysDiff(offer?.paymentByDate, newPaymentByDate);
  const liftingDiff = daysDiff(offer?.liftingByDate, newLiftingByDate);
  const isPaymentExtended = paymentDiff > 0;
  const isLiftingExtended = liftingDiff > 0;
  const isPaused = offer?.status === 'paused';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.5)' }}>
      <div className="w-full max-w-lg rounded-2xl shadow-2xl overflow-hidden" style={{ background: '#fffbf5', border: '1px solid #f3e8d0', maxHeight: '90vh', overflowY: 'auto' }}>
        <div className="px-6 py-4 flex items-center gap-3" style={{ background: 'linear-gradient(135deg, #fff7ed, #fffbf5)', borderBottom: '1px solid #f3e8d0' }}>
          <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #10b981, #059669)' }}>
            <Icon name="RefreshCw" size={20} color="#ffffff" />
          </div>
          <div>
            <h3 className="text-base font-bold" style={{ color: '#1a1a1a' }}>Resume Offer</h3>
            <p className="text-xs mt-0.5" style={{ color: '#6b7280' }}>
              {offer?.id} · {offer?.factoryName} · {offer?.grade} · {offer?.quantity?.toLocaleString('en-IN')} MT
            </p>
          </div>
          <button onClick={onCancel} className="ml-auto p-1.5 rounded-lg hover:bg-orange-50 transition-colors">
            <Icon name="X" size={18} color="#9ca3af" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-5">
          {/* Date Inputs */}
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide mb-3" style={{ color: '#92400e' }}>Update Dates</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[
                { label: 'Payment By Date', value: newPaymentByDate, onChange: setNewPaymentByDate, extended: isPaymentExtended },
                { label: 'Lifting By Date', value: newLiftingByDate, onChange: setNewLiftingByDate, extended: isLiftingExtended },
              ]?.map(({ label, value, onChange, extended }) => (
                <div key={label}>
                  <label className="block text-xs font-medium mb-1.5" style={{ color: '#374151' }}>{label}</label>
                  <div className="relative">
                    <div className="w-full px-3 py-2 rounded-lg text-sm" style={{ background: '#fff', border: `1px solid ${extended ? '#f97316' : '#f3e8d0'}`, color: value ? '#1a1a1a' : '#9ca3af', boxShadow: extended ? '0 0 0 2px #fed7aa' : 'none', pointerEvents: 'none', position: 'relative', zIndex: 1 }}>
                      {value ? formatDate(value) : 'DD/MMM/YYYY'}
                    </div>
                    <input type="date" value={value} onChange={(e) => onChange(e?.target?.value)} style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', opacity: 0, cursor: 'pointer', zIndex: 2 }} />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Date Comparison */}
          <div className="rounded-xl overflow-hidden" style={{ border: '1px solid #f3e8d0' }}>
            <div className="px-4 py-2.5 flex items-center gap-2" style={{ background: '#fef3e2', borderBottom: '1px solid #f3e8d0' }}>
              <Icon name="Calendar" size={14} color="#ea580c" />
              <span className="text-xs font-semibold" style={{ color: '#92400e' }}>Date Comparison</span>
            </div>
            <div className="grid grid-cols-3 text-xs" style={{ background: '#fffbf5' }}>
              <div className="px-4 py-2 font-semibold" style={{ color: '#6b7280', borderBottom: '1px solid #f3e8d0' }}>Field</div>
              <div className="px-4 py-2 font-semibold text-center" style={{ color: '#6b7280', borderBottom: '1px solid #f3e8d0', borderLeft: '1px solid #f3e8d0' }}>Original</div>
              <div className="px-4 py-2 font-semibold text-center" style={{ color: '#6b7280', borderBottom: '1px solid #f3e8d0', borderLeft: '1px solid #f3e8d0' }}>New</div>

              <div className="px-4 py-3 font-medium" style={{ color: '#374151', borderBottom: '1px solid #f3e8d0' }}>Payment By</div>
              <div className="px-4 py-3 text-center" style={{ color: '#374151', borderBottom: '1px solid #f3e8d0', borderLeft: '1px solid #f3e8d0' }}>{formatDate(offer?.paymentByDate)}</div>
              <div className="px-4 py-3 text-center font-semibold" style={{ color: isPaymentExtended ? '#ea580c' : paymentDiff < 0 ? '#059669' : '#374151', borderBottom: '1px solid #f3e8d0', borderLeft: '1px solid #f3e8d0', background: isPaymentExtended ? '#fff7ed' : paymentDiff < 0 ? '#f0fdf4' : 'transparent' }}>
                {formatDate(newPaymentByDate)}
                {isPaymentExtended && <span className="ml-1 text-xs font-bold" style={{ color: '#ea580c' }}>+{paymentDiff}d ↑</span>}
                {paymentDiff < 0 && <span className="ml-1 text-xs font-bold" style={{ color: '#059669' }}>{paymentDiff}d ↓</span>}
              </div>

              <div className="px-4 py-3 font-medium" style={{ color: '#374151' }}>Lifting By</div>
              <div className="px-4 py-3 text-center" style={{ color: '#374151', borderLeft: '1px solid #f3e8d0' }}>{formatDate(offer?.liftingByDate)}</div>
              <div className="px-4 py-3 text-center font-semibold" style={{ color: isLiftingExtended ? '#ea580c' : liftingDiff < 0 ? '#059669' : '#374151', borderLeft: '1px solid #f3e8d0', background: isLiftingExtended ? '#fff7ed' : liftingDiff < 0 ? '#f0fdf4' : 'transparent' }}>
                {formatDate(newLiftingByDate)}
                {isLiftingExtended && <span className="ml-1 text-xs font-bold" style={{ color: '#ea580c' }}>+{liftingDiff}d ↑</span>}
                {liftingDiff < 0 && <span className="ml-1 text-xs font-bold" style={{ color: '#059669' }}>{liftingDiff}d ↓</span>}
              </div>
            </div>
          </div>

          {/* Info note */}
          <div className="rounded-xl p-3 flex items-start gap-2" style={{ background: isPaused ? '#f0fdf4' : '#eff6ff', border: `1px solid ${isPaused ? '#bbf7d0' : '#bfdbfe'}` }}>
            <Icon name="Info" size={14} color={isPaused ? '#059669' : '#3b82f6'} />
            <p className="text-xs" style={{ color: isPaused ? '#14532d' : '#1e3a5f' }}>
              {isPaused
                ? 'Your offer will be immediately relisted in the Marketplace.'
                : 'On confirming, this offer will be sent to the Quota Manager for fresh approval with status "Pending for Resume".'}
            </p>
          </div>

          <div className="flex gap-3 pt-1">
            <button onClick={onCancel} className="flex-1 py-2.5 rounded-xl text-sm font-semibold transition-all" style={{ background: '#f3f4f6', color: '#374151', border: '1px solid #e5e7eb' }}>
              Cancel
            </button>
            <button onClick={() => onConfirm({ newPaymentByDate, newLiftingByDate })} className="flex-1 py-2.5 rounded-xl text-sm font-bold text-white shadow-md transition-all" style={{ background: 'linear-gradient(135deg, #10b981, #059669)' }}>
              Confirm &amp; Resume
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Main Component ────────────────────────────────────────────────────────────
const MySellOffersTab = () => {
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pauseModal, setPauseModal] = useState(null);
  const [withdrawModal, setWithdrawModal] = useState(null);
  const [resumeModal, setResumeModal] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [preset, setPreset] = useState('this_fy');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const { user } = useAuth();

  const fetchOffers = useCallback(async () => {
    if (!user?.email) {
      setOffers([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const { data, error: fetchError } = await supabase
        ?.from('quotas')
        ?.select('*')
        ?.eq('seller_email', user?.email)
        ?.order('created_at', { ascending: false });

      if (fetchError) {
        console.error('Error fetching sell offers:', fetchError);
        setError('Unable to load your sell offers. Please try again.');
        setOffers([]);
      } else {
        setOffers((data || [])?.map(mapQuotaToOffer));
      }
    } catch (err) {
      console.error('Unexpected error:', err);
      setError('Something went wrong. Please refresh the page.');
      setOffers([]);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchOffers();
    const channel = supabase
      ?.channel('my_sell_offers_realtime')
      ?.on('postgres_changes', { event: '*', schema: 'public', table: 'quotas' }, () => {
        fetchOffers();
      })
      ?.subscribe();
    return () => supabase?.removeChannel(channel);
  }, [fetchOffers]);

  // ── Pause ────────────────────────────────────────────────────────────────
  const handlePauseConfirm = async () => {
    if (!pauseModal) return;
    try {
      await supabase?.from('quotas')?.update({ availability_status: 'paused' })?.eq('id', pauseModal?.dbId);
      setOffers((prev) => prev?.map((o) => o?.dbId === pauseModal?.dbId ? { ...o, status: 'paused' } : o));
    } catch (err) {
      console.error('Pause error:', err);
    }
    setPauseModal(null);
  };

  // ── Withdraw ─────────────────────────────────────────────────────────────
  const handleWithdrawConfirm = async () => {
    if (!withdrawModal) return;
    try {
      await supabase?.from('quotas')?.update({ availability_status: 'withdrawn' })?.eq('id', withdrawModal?.dbId);
      setOffers((prev) => prev?.map((o) => o?.dbId === withdrawModal?.dbId ? { ...o, status: 'withdrawn' } : o));
      sendSellOfferEmail('withdrawal', withdrawModal?.sellerEmail, {
        id: withdrawModal?.id,
        sellerName: user?.email || 'Seller',
        factoryName: withdrawModal?.factoryName,
        grade: withdrawModal?.grade,
        quantity: withdrawModal?.quantity,
        offeredRate: withdrawModal?.offeredRate,
      });
    } catch (err) {
      console.error('Withdraw error:', err);
    }
    setWithdrawModal(null);
  };

  // ── Resume ───────────────────────────────────────────────────────────────
  const handleResumeConfirm = async ({ newPaymentByDate, newLiftingByDate }) => {
    if (!resumeModal) return;
    const isPaused = resumeModal?.status === 'paused';
    try {
      const updatePayload = {
        available_from_date: newPaymentByDate,
        offer_valid_until: newLiftingByDate,
        original_payment_by_date: resumeModal?.paymentByDate,
        original_lifting_by_date: resumeModal?.liftingByDate,
      };

      if (isPaused) {
        updatePayload.availability_status = 'available';
        updatePayload.is_resume_request = false;
      } else {
        updatePayload.availability_status = 'reserved';
        updatePayload.is_resume_request = true;
      }

      await supabase?.from('quotas')?.update(updatePayload)?.eq('id', resumeModal?.dbId);
      setOffers((prev) =>
        prev?.map((o) =>
          o?.dbId === resumeModal?.dbId
            ? {
                ...o,
                status: isPaused ? 'available' : 'reserved',
                paymentByDate: newPaymentByDate,
                liftingByDate: newLiftingByDate,
                isResumeRequest: !isPaused,
              }
            : o
        )
      );
    } catch (err) {
      console.error('Resume error:', err);
    }
    setResumeModal(null);
  };

  // ── Filter logic ─────────────────────────────────────────────────────────
  const { from: dateFrom, to: dateTo } = getDateRange(preset, customFrom, customTo);

  const filteredOffers = offers?.filter(offer => {
    const s = searchText?.toLowerCase() || '';
    const matchesSearch = !s ||
      offer?.factoryName?.toLowerCase()?.includes(s) ||
      offer?.grade?.toLowerCase()?.includes(s) ||
      String(offer?.id || '')?.toLowerCase()?.includes(s);

    const matchesStatus =
      statusFilter === 'all' ||
      (statusFilter === 'pending' && (offer?.status === 'reserved' || offer?.status === 'pending_approval')) ||
      (statusFilter === 'available' && offer?.status === 'available') ||
      (statusFilter === 'paused' && offer?.status === 'paused') ||
      (statusFilter === 'withdrawn' && (offer?.status === 'withdrawn' || offer?.status === 'sold')) ||
      (statusFilter === 'rejected' && offer?.status === 'rejected');

    const offerDate = offer?.created_at ? new Date(offer.created_at) : null;
    const matchesDate =
      (!dateFrom && !dateTo) ||
      (offerDate &&
        (!dateFrom || offerDate >= dateFrom) &&
        (!dateTo || offerDate <= dateTo));

    return matchesSearch && matchesStatus && matchesDate;
  });

  const StatusBadge = ({ status }) => {
    const cfg = STATUS_CONFIG?.[status] || { label: status, bg: '#f3f4f6', color: '#374151', dot: '#9ca3af' };
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold" style={{ background: cfg?.bg, color: cfg?.color }}>
        <span className="w-1.5 h-1.5 rounded-full inline-block" style={{ background: cfg?.dot }} />
        {cfg?.label}
      </span>
    );
  };

  const renderActionButtons = (offer) => {
    const status = offer?.status;
    if (status === 'available') {
      return (
        <div className="flex flex-col gap-1.5">
          <button
            onClick={() => setPauseModal(offer)}
            className="px-3 py-1.5 rounded-lg text-xs font-bold text-white transition-all shadow-sm hover:shadow-md"
            style={{ background: 'linear-gradient(135deg, #ca8a04, #a16207)' }}
            title="Pause – temporary hide, can resume anytime"
          >
            ⏸ Pause (temporary hide)
          </button>
          <button
            onClick={() => setWithdrawModal(offer)}
            className="px-3 py-1.5 rounded-lg text-xs font-bold text-white transition-all shadow-sm hover:shadow-md"
            style={{ background: 'linear-gradient(135deg, #ef4444, #dc2626)' }}
            title="Withdraw – strong removal, needs QM re-approval to relist"
          >
            ✕ Withdraw (serious action)
          </button>
        </div>
      );
    }
    if (status === 'reserved' || status === 'pending_approval') {
      return (
        <button
          onClick={() => setWithdrawModal(offer)}
          className="px-3 py-1.5 rounded-lg text-xs font-bold text-white transition-all shadow-sm hover:shadow-md"
          style={{ background: 'linear-gradient(135deg, #ef4444, #dc2626)' }}
          title="Withdraw – strong removal"
        >
          ✕ Withdraw
        </button>
      );
    }
    if (status === 'paused') {
      return (
        <div className="flex flex-col gap-1.5">
          <button
            onClick={() => setResumeModal(offer)}
            className="px-3 py-1.5 rounded-lg text-xs font-bold text-white transition-all shadow-sm hover:shadow-md"
            style={{ background: 'linear-gradient(135deg, #10b981, #059669)' }}
            title="Resume – goes back live immediately"
          >
            ▶ Resume (go live)
          </button>
          <button
            onClick={() => setWithdrawModal(offer)}
            className="px-3 py-1.5 rounded-lg text-xs font-bold text-white transition-all shadow-sm hover:shadow-md"
            style={{ background: 'linear-gradient(135deg, #ef4444, #dc2626)' }}
            title="Withdraw – strong removal, needs QM re-approval to relist"
          >
            ✕ Withdraw (serious action)
          </button>
        </div>
      );
    }
    if (status === 'withdrawn' || status === 'sold') {
      return (
        <button
          onClick={() => setResumeModal(offer)}
          className="px-3 py-1.5 rounded-lg text-xs font-bold text-white transition-all shadow-sm hover:shadow-md"
          style={{ background: 'linear-gradient(135deg, #10b981, #059669)' }}
          title="Resume – sends to QM for re-approval"
        >
          ↩ Resume / Reschedule
        </button>
      );
    }
    return <span className="text-xs" style={{ color: '#9ca3af' }}>—</span>;
  };

  return (
    <div className="rounded-2xl overflow-hidden shadow-sm" style={{ background: '#fffbf5', border: '1px solid #f3e8d0' }}>

      {/* Header */}
      <div className="px-6 py-5 flex items-center gap-3" style={{ borderBottom: '1px solid #f3e8d0', background: 'linear-gradient(135deg, #fff7ed, #fffbf5)' }}>
        <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #f97316, #ea580c)' }}>
          <Icon name="Tag" size={20} color="#ffffff" />
        </div>
        <div>
          <h2 className="text-lg font-bold" style={{ color: '#1a1a1a' }}>My Sell Offers</h2>
          <p className="text-xs mt-0.5" style={{ color: '#9a3412' }}>
            {loading ? 'Loading...' : `${filteredOffers?.length} offer${filteredOffers?.length !== 1 ? 's' : ''} submitted`}
          </p>
        </div>
        <button onClick={fetchOffers} className="ml-auto p-2 rounded-lg transition-colors hover:bg-orange-50" title="Refresh">
          <Icon name="RefreshCw" size={16} color="#f97316" />
        </button>
      </div>

      {/* Search + Status Filter + Date Filter */}
      {!loading && !error && user && (
        <div className="px-6 py-4 flex flex-col gap-3" style={{ borderBottom: '1px solid #f3e8d0', background: '#fffdf9' }}>
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none">
                <Icon name="Search" size={16} color="#9ca3af" />
              </span>
              <input
                type="text"
                placeholder="Search by factory, grade, or offer ID..."
                value={searchText}
                onChange={e => setSearchText(e.target.value)}
                className="w-full pl-9 pr-4 py-2.5 rounded-xl text-sm outline-none"
                style={{ border: '1px solid #f3e8d0', color: '#1e293b', backgroundColor: '#ffffff' }}
              />
            </div>
            <select
              value={statusFilter}
              onChange={e => setStatusFilter(e.target.value)}
              className="py-2.5 px-3 rounded-xl text-sm outline-none"
              style={{ border: '1px solid #f3e8d0', color: '#374151', backgroundColor: '#ffffff', minWidth: '160px' }}
            >
              <option value="all">All Status</option>
              <option value="pending">Pending Approval</option>
              <option value="available">Approved &amp; Live</option>
              <option value="paused">Paused</option>
              <option value="withdrawn">Withdrawn</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>
          <DateRangeFilter
            preset={preset}
            onPresetChange={setPreset}
            customFrom={customFrom}
            onCustomFromChange={setCustomFrom}
            customTo={customTo}
            onCustomToChange={setCustomTo}
          />
        </div>
      )}

      {/* Status Legend */}
      {!loading && !error && user && offers?.length > 0 && (
        <div className="px-6 py-3 space-y-2" style={{ borderBottom: '1px solid #f3e8d0', background: '#fffdf9' }}>
          <div className="flex flex-wrap gap-3">
            {[
              { status: 'reserved', label: 'Pending for Approval' },
              { status: 'available', label: 'Approved & Live' },
              { status: 'paused', label: 'Paused' },
              { status: 'withdrawn', label: 'Withdrawn' },
              { status: 'rejected', label: 'Rejected' },
            ]?.map(({ status, label }) => {
              const cfg = STATUS_CONFIG?.[status];
              return (
                <span key={status} className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium" style={{ background: cfg?.bg, color: cfg?.color }}>
                  <span className="w-1.5 h-1.5 rounded-full" style={{ background: cfg?.dot }} />
                  {label}
                </span>
              );
            })}
          </div>
          <div className="flex flex-wrap gap-3 text-xs" style={{ color: '#6b7280' }}>
            <span className="flex items-center gap-1">
              <span className="w-2 h-2 rounded-full inline-block" style={{ background: '#ca8a04' }} />
              <strong style={{ color: '#a16207' }}>Pause</strong> = temporary hide, resume anytime without re-approval
            </span>
            <span className="flex items-center gap-1">
              <span className="w-2 h-2 rounded-full inline-block" style={{ background: '#dc2626' }} />
              <strong style={{ color: '#dc2626' }}>Withdraw</strong> = strong removal, QM re-approval needed to relist
            </span>
          </div>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="py-16 flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-2 border-orange-300 border-t-orange-500 rounded-full animate-spin" />
          <p className="text-sm" style={{ color: '#9ca3af' }}>Loading your sell offers...</p>
        </div>
      )}

      {/* Error State */}
      {!loading && error && (
        <div className="py-12 flex flex-col items-center gap-3 px-6">
          <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ background: '#fee2e2' }}>
            <Icon name="AlertCircle" size={24} color="#dc2626" />
          </div>
          <p className="font-semibold text-sm" style={{ color: '#374151' }}>{error}</p>
          <button onClick={fetchOffers} className="px-4 py-2 rounded-lg text-sm font-semibold text-white" style={{ background: '#f97316' }}>
            Try Again
          </button>
        </div>
      )}

      {/* Not logged in */}
      {!loading && !error && !user && (
        <div className="py-16 flex flex-col items-center gap-3">
          <div className="w-14 h-14 rounded-full flex items-center justify-center" style={{ background: '#fef3c7' }}>
            <Icon name="User" size={28} color="#f59e0b" />
          </div>
          <p className="font-semibold" style={{ color: '#374151' }}>Please log in to view your sell offers</p>
        </div>
      )}

      {/* Desktop Table */}
      {!loading && !error && user && filteredOffers?.length > 0 && (
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr style={{ background: '#fef3e2', borderBottom: '1px solid #f3e8d0' }}>
                {['Offer ID', 'Factory Name', 'Grade', 'Qty (MT)', 'Rate (₹/Ton)', 'Quality', 'Payment By', 'Lifting By', 'Validity', 'Status', 'Actions']?.map((h) => (
                  <th key={h} className="px-4 py-3 text-left font-semibold whitespace-nowrap" style={{ color: '#92400e', fontSize: '0.75rem', letterSpacing: '0.03em' }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredOffers?.map((offer, idx) => (
                <tr key={offer?.dbId} style={{ background: idx % 2 === 0 ? '#fffbf5' : '#fff7ed', borderBottom: '1px solid #f3e8d0' }}>
                  <td className="px-4 py-3 font-mono font-semibold whitespace-nowrap" style={{ color: '#ea580c', fontSize: '0.8rem' }}>{offer?.id}</td>
                  <td className="px-4 py-3 font-medium" style={{ color: '#1a1a1a' }}>{offer?.factoryName}</td>
                  <td className="px-4 py-3 whitespace-nowrap" style={{ color: '#374151' }}>{offer?.grade}</td>
                  <td className="px-4 py-3 whitespace-nowrap font-semibold" style={{ color: '#374151' }}>{offer?.quantity?.toLocaleString('en-IN')}</td>
                  <td className="px-4 py-3 whitespace-nowrap font-semibold" style={{ color: '#374151' }}>₹{offer?.offeredRate?.toLocaleString('en-IN')}</td>
                  <td className="px-4 py-3 whitespace-nowrap" style={{ color: '#374151' }}>{offer?.quality}</td>
                  <td className="px-4 py-3 whitespace-nowrap" style={{ color: '#374151' }}>{formatDate(offer?.paymentByDate)}</td>
                  <td className="px-4 py-3 whitespace-nowrap" style={{ color: '#374151' }}>{formatDate(offer?.liftingByDate)}</td>
                  <td className="px-4 py-3 whitespace-nowrap text-center font-semibold" style={{ color: '#374151' }}>
                    {offer?.offerValidity != null ? `${offer?.offerValidity}d` : '—'}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <StatusBadge status={offer?.status} />
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    {renderActionButtons(offer)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Mobile Cards */}
      {!loading && !error && user && filteredOffers?.length > 0 && (
        <div className="md:hidden divide-y" style={{ borderColor: '#f3e8d0' }}>
          {filteredOffers?.map((offer) => {
            const cfg = STATUS_CONFIG?.[offer?.status] || STATUS_CONFIG?.['rejected'];
            return (
              <div key={offer?.dbId} className="p-4" style={{ background: '#fffbf5' }}>
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <p className="font-mono font-bold text-xs" style={{ color: '#ea580c' }}>{offer?.id}</p>
                    <p className="font-semibold text-sm mt-0.5" style={{ color: '#1a1a1a' }}>{offer?.factoryName}</p>
                  </div>
                  <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold" style={{ background: cfg?.bg, color: cfg?.color }}>
                    <span className="w-1.5 h-1.5 rounded-full" style={{ background: cfg?.dot }} />
                    {cfg?.label}
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs mb-3" style={{ color: '#6b7280' }}>
                  <span>Grade: <strong style={{ color: '#374151' }}>{offer?.grade}</strong></span>
                  <span>Qty: <strong style={{ color: '#374151' }}>{offer?.quantity?.toLocaleString('en-IN')} MT</strong></span>
                  <span>Rate: <strong style={{ color: '#374151' }}>₹{offer?.offeredRate?.toLocaleString('en-IN')}/Ton</strong></span>
                  <span>Quality: <strong style={{ color: '#374151' }}>{offer?.quality}</strong></span>
                  <span>Pay By: <strong style={{ color: '#374151' }}>{formatDate(offer?.paymentByDate)}</strong></span>
                  <span>Lift By: <strong style={{ color: '#374151' }}>{formatDate(offer?.liftingByDate)}</strong></span>
                </div>
                <div className="flex gap-2 flex-wrap">
                  {renderActionButtons(offer)}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* No match state */}
      {!loading && !error && user && offers?.length > 0 && filteredOffers?.length === 0 && (
        <div className="py-16 flex flex-col items-center gap-3">
          <div className="w-14 h-14 rounded-full flex items-center justify-center" style={{ background: '#fef3c7' }}>
            <Icon name="Search" size={28} color="#f59e0b" />
          </div>
          <p className="font-semibold" style={{ color: '#374151' }}>No matching offers</p>
          <p className="text-sm" style={{ color: '#9ca3af' }}>Try adjusting your search, status filter, or date range</p>
        </div>
      )}

      {/* Empty state — no offers at all */}
      {!loading && !error && user && offers?.length === 0 && (
        <div className="py-16 flex flex-col items-center gap-3">
          <div className="w-14 h-14 rounded-full flex items-center justify-center" style={{ background: '#fef3c7' }}>
            <Icon name="Tag" size={28} color="#f59e0b" />
          </div>
          <p className="font-semibold" style={{ color: '#374151' }}>No sell offers yet</p>
          <p className="text-sm" style={{ color: '#9ca3af' }}>Submit your first offer via "Sell Your Sugar"</p>
        </div>
      )}

      {/* Modals */}
      {pauseModal && <PauseModal offer={pauseModal} onConfirm={handlePauseConfirm} onCancel={() => setPauseModal(null)} />}
      {withdrawModal && <WithdrawModal offer={withdrawModal} onConfirm={handleWithdrawConfirm} onCancel={() => setWithdrawModal(null)} />}
      {resumeModal && <ResumeModal offer={resumeModal} onConfirm={handleResumeConfirm} onCancel={() => setResumeModal(null)} />}
    </div>
  );
};

export default MySellOffersTab;
